import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";

interface DarkModeContextType {
  darkMode: boolean;
  toggleDarkMode: () => Promise<void>;
}

const DarkModeContext = createContext<DarkModeContextType>({
  darkMode: false,
  toggleDarkMode: async () => {},
});

export const useDarkMode = () => useContext(DarkModeContext);

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [darkMode, setDarkMode] = useState(false);
  const { user } = useAuth();

  // Load user's dark mode preference from Firestore
  useEffect(() => {
    const loadDarkModePreference = async () => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const prefersDarkMode = userData.darkMode || false;
            setDarkMode(prefersDarkMode);

            // Apply dark mode to document
            if (prefersDarkMode) {
              document.documentElement.classList.add("dark");
            } else {
              document.documentElement.classList.remove("dark");
            }
          }
        } catch (error) {
          // console.error("Error loading dark mode preference:", error);
        }
      } else {
        // Handle when no user is logged in - use system preference or default
        const prefersDarkMode = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        setDarkMode(prefersDarkMode);
        if (prefersDarkMode) {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      }
    };

    loadDarkModePreference();
  }, [user]);

  // Implement the actual toggleDarkMode function
  const toggleDarkMode = async () => {
    if (!user) return;

    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    try {
      // Update Firestore
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        darkMode: newDarkMode,
      });

      // Apply dark mode to document
      if (newDarkMode) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    } catch (error) {
      // console.error("Error updating dark mode preference:", error);
    }

    return Promise.resolve();
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};
